
import { supabase } from "@/integrations/supabase/client";
import { detectAccommodationType } from './mappings/AccommodationMapping';
import { detectExperienceType } from './mappings/ExperienceMapping';
import { detectLocation } from './mappings/LocationMapping';
import { applyLocationFilter } from './filters/LocationFilter';
import { applyAccommodationFilter } from './filters/AccommodationFilter';
import { applyExperienceFilter } from './filters/ExperienceFilter';
import { applyCriteriaFilter } from './filters/CriteriaFilter';
import { detectPriceConstraints, applyPriceFilter } from './filters/PriceFilter';
import { getUsedTerms, applyBroadSearch, validateVacationData } from './utils/SearchUtils';

export const performBasicSearch = async (query: string) => {
  try {
    console.log('Performing enhanced basic search for:', query);
    
    const searchTerms = query.toLowerCase().split(' ');
    
    let dbQuery = supabase
      .from('Complete feed')
      .select('*')
      .not('imageURL', 'is', null)
      .not('name', 'is', null);

    // Step 1: Detect accommodation type
    const detectedAccommodationType = detectAccommodationType(searchTerms);

    // Step 2: Detect experience type
    const detectedExperienceType = detectExperienceType(searchTerms);

    // Step 3: Detect location (city, region, or country)
    const detectedLocation = detectLocation(searchTerms);

    // Step 4: Detect price constraints
    const priceConstraints = detectPriceConstraints(searchTerms);

    // Apply location filtering
    if (detectedLocation) {
      dbQuery = applyLocationFilter(dbQuery, detectedLocation);
      
      if (detectedLocation.type === 'city') {
        console.log(`Filtering by city: ${detectedLocation.info.city} in ${detectedLocation.info.region}, ${detectedLocation.info.country}`);
      } else if (detectedLocation.type === 'region') {
        console.log(`Filtering by region: ${detectedLocation.info.region} in ${detectedLocation.info.country}`);
      } else if (detectedLocation.type === 'country') {
        console.log(`Filtering by country: ${detectedLocation.info.country}`);
      }
    }

    // Apply price filtering
    if (priceConstraints) {
      dbQuery = applyPriceFilter(dbQuery, priceConstraints);
    }

    // Apply accommodation type filtering
    if (detectedAccommodationType) {
      dbQuery = applyAccommodationFilter(dbQuery, detectedAccommodationType);
    }

    // Apply experience type filtering
    if (detectedExperienceType) {
      dbQuery = applyExperienceFilter(dbQuery, detectedExperienceType);
    }

    // Apply remaining criteria within the detected filters
    const usedTerms = getUsedTerms(detectedAccommodationType, detectedExperienceType, detectedLocation);
    const remainingTerms = searchTerms.filter(term => 
      !usedTerms.includes(term) && 
      // Filter out price-related terms that were already handled
      !['goedkoop', 'budget', 'voordelig', 'betaalbaar', 'luxe', 'duur', 'max', 'maximaal', 'onder'].includes(term) &&
      !term.includes('€') &&
      !/^\d+$/.test(term)
    );
    
    if (remainingTerms.length > 0 && !detectedAccommodationType && !detectedExperienceType) {
      dbQuery = applyCriteriaFilter(dbQuery, remainingTerms);
    }

    // If no specific filters detected, use original broad search
    if (!detectedLocation && !detectedAccommodationType && !detectedExperienceType && !priceConstraints) {
      dbQuery = applyBroadSearch(dbQuery, searchTerms);
    }

    const { data, error } = await dbQuery.limit(100);
    
    if (error) throw error;
    
    console.log(`Enhanced basic search found ${data?.length || 0} results for "${query}"`);
    
    return validateVacationData(data);
  } catch (error: any) {
    console.error('Enhanced basic search error:', error);
    throw error;
  }
};
